import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import SpinModal from './SpinModal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { useAuth } from '../AuthContext';

function TodoPokemonGamesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [reversed, setReversed] = useState(false);
  const [search, setSearch] = useState('');
  const [showSpin, setShowSpin] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addPoster, setAddPoster] = useState(null);
  const [addPosterPreview, setAddPosterPreview] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addStatus, setAddStatus] = useState('');

  const [editingGame, setEditingGame] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editPoster, setEditPoster] = useState(null);
  const [editPosterPreview, setEditPosterPreview] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [convertingId, setConvertingId] = useState(null);

  const fetchGames = async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_pokemon_games?order=created_at.desc&select=*`, {
        headers: supabaseHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setGames(data || []);
    } catch (err) {
      console.error('Failed to fetch todo pokemon games:', err);
    }
  };

  useEffect(() => { fetchGames(); }, []);

  const shuffle = () => {
    setGames((prev) => [...prev].sort(() => Math.random() - 0.5));
  };

  const openAdd = () => {
    setAddTitle('');
    setAddPoster(null);
    setAddPosterPreview(null);
    setAddStatus('');
    setShowAdd(true);
  };

  const closeAdd = () => setShowAdd(false);

  const openEdit = (game) => {
    setEditingGame(game);
    setEditTitle(game.title);
    setEditPoster(null);
    setEditPosterPreview(null);
    setEditStatus('');
  };

  const closeEdit = () => setEditingGame(null);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editTitle) { setEditStatus('Enter a title.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      let posterUrl = null;
      if (editPoster) posterUrl = await uploadPoster(editPoster);

      const payload = { title: editTitle };
      if (posterUrl) payload.poster_url = posterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_pokemon_games?id=eq.${editingGame.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Update failed');

      setEditStatus('Saved :D');
      setTimeout(() => { setEditingGame(null); fetchGames(); }, 1000);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditDelete = async () => {
    if (!window.confirm(`Delete "${editingGame.title}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_pokemon_games?id=eq.${editingGame.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setEditingGame(null);
      fetchGames();
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addTitle) { setAddStatus('Enter a title.'); return; }
    setAddLoading(true);
    setAddStatus('');
    try {
      let posterUrl = null;
      if (addPoster) posterUrl = await uploadPoster(addPoster);

      const payload = { title: addTitle };
      if (posterUrl) payload.poster_url = posterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_pokemon_games`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to create');

      setAddStatus('Saved :D');
      setTimeout(() => {
        setShowAdd(false);
        fetchGames();
      }, 1000);
    } catch (err) {
      console.error(err);
      setAddStatus(`Something went wrong: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  // Pokémon games don't have a review form (no rating/thoughts) - converting a to-do entry just
  // means moving it straight into the real `pokemon_games` list, then opening that game's page.
  const convertToGame = async (game) => {
    if (!user || convertingId) return;
    setConvertingId(game.id);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_games`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify({ name: game.title, poster_url: game.poster_url || null }),
      });
      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();
      const newGame = Array.isArray(data) ? data[0] : data;

      await fetch(`${SUPABASE_URL}/rest/v1/todo_pokemon_games?id=eq.${game.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });

      navigate(`/pokemon/${newGame.id}`);
    } catch (err) {
      console.error(err);
      setConvertingId(null);
    }
  };

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1 onClick={() => navigate('/pokemon')} style={{ cursor: 'pointer' }}>Pokémon</h1>
          <span className="header-subtitle">To-Do</span>
        </div>
        <div className="controls">
          <button onClick={() => navigate('/pokemon')} className="header-nav-btn">←</button>
          {user && <button onClick={openAdd} style={{ width: 'fit-content' }}>Add +</button>}
          <input type="text" placeholder="Search by title..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <button onClick={shuffle} style={{ width: 'fit-content' }}>Randomize</button>
          <button onClick={() => setReversed((r) => !r)} className={reversed ? 'btn-active' : ''} style={{ width: 'fit-content' }}>{reversed ? 'Newest' : 'Oldest'}</button>
          <button onClick={() => setShowSpin(true)} style={{ width: 'fit-content' }}>Spin</button>
        </div>
      </div>

      <div className="todo-grid">
        {(reversed ? [...games].reverse() : games).filter(g => g.title.toLowerCase().includes(search.toLowerCase())).map((game, idx) => (
          <div
            className="todo-card"
            key={game.id}
            style={{ '--index': idx }}
            onDoubleClick={user ? () => convertToGame(game) : undefined}
          >
            {game.poster_url && (
              <img src={game.poster_url} alt={game.title} className="todo-card-poster" />
            )}
            <div
              className="todo-card-title"
              onDoubleClick={user ? (e) => { e.stopPropagation(); openEdit(game); } : undefined}
            >
              {convertingId === game.id ? 'Working...' : game.title}
            </div>
          </div>
        ))}
      </div>

      {games.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <Modal onClose={closeAdd}>
            <h2 style={{ margin: 0, fontFamily: 'angelWish', fontSize: '2rem', letterSpacing: '0.06em' }}>Add</h2>
            <form onSubmit={handleAdd} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Title *</label>
                <input type="text" value={addTitle} onChange={(e) => setAddTitle(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Poster</label>
                {addPosterPreview && <img src={addPosterPreview} alt="preview" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />}
                <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setAddPoster(f); setAddPosterPreview(f ? URL.createObjectURL(f) : null); }} />
              </div>
              {addStatus && <p style={{ margin: 0, fontWeight: 'bold' }}>{addStatus}</p>}
              <div className="edit-modal-actions">
                <button type="submit" className="form-submit" disabled={addLoading}>
                  {addLoading ? 'Loading...' : 'Add'}
                </button>
                <button type="button" className="form-submit" onClick={closeAdd} style={{ background: 'white', color: 'black', WebkitTextFillColor: 'black' }}>
                  Cancel
                </button>
              </div>
            </form>
        </Modal>
      )}

      {editingGame && (
        <Modal onClose={closeEdit}>
          <h2 style={{ margin: 0, fontFamily: 'angelWish', fontSize: '2rem', letterSpacing: '0.06em' }}>Edit</h2>
          <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div className="form-group">
              <label>Title *</label>
              <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Poster</label>
              {(editPosterPreview || (editingGame.poster_url && !editPoster)) && (
                <img src={editPosterPreview || editingGame.poster_url} alt="preview" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
              )}
              <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setEditPoster(f); setEditPosterPreview(f ? URL.createObjectURL(f) : null); }} />
            </div>
            {editStatus && <p style={{ margin: 0, fontWeight: 'bold' }}>{editStatus}</p>}
            <div className="edit-modal-actions">
              <button type="button" className="form-submit btn-cancel" onClick={closeEdit}>Cancel</button>
              <button type="button" className="form-submit btn-delete" onClick={handleEditDelete}>Delete</button>
              <button type="submit" className="form-submit" disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showSpin && (
        <SpinModal
          items={(reversed ? [...games].reverse() : games).filter(g => g.title.toLowerCase().includes(search.toLowerCase()))}
          onClose={() => setShowSpin(false)}
        />
      )}
    </div>
  );
}

export default TodoPokemonGamesList;
